import { osmAddressLines } from "./address.ts";
import { debugSelection } from "../debug.ts";
import {
  OSM_SPATIAL_RANK,
  type OsmLookupElement,
  osmSpatialMatchLabel,
  type OsmSpatialRank,
} from "./spatial.ts";
import { containingBoundaryValue, firstTag } from "./tags.ts";
import type { LocatedOsmElement, OsmElement, OsmTags } from "./types.ts";

export type RankedOsmElements =
  | {
    status: "matched";
    best: LocatedOsmElement;
    alternatives: LocatedOsmElement[];
  }
  | { status: "empty" };

interface OsmCandidateEvidence {
  name: string;
  alternateNames: string[];
  spatialRank: OsmSpatialRank;
  spatialScore: number;
  hasWebsite: boolean;
  hasOpeningHours: boolean;
  hasAddress: boolean;
  hasLocalFeatureTag: boolean;
  hasPoiCategory: boolean;
  containingBoundaryValue: string | undefined;
  containingBoundaryPenalty: number;
  isRouteRelation: boolean;
  isUnnamedResidentialLanduse: boolean;
}

interface OsmCandidateScore {
  total: number;
  reasons: string[];
}

export function rankOsmElements(
  elements: OsmLookupElement[],
  hintName: string | undefined,
): RankedOsmElements {
  const normalizedHint = normalized(hintName);
  const candidates = elements.map((element, index) => ({
    element,
    index,
    evidence: osmCandidateEvidence(element),
  })).map((candidate) => ({
    ...candidate,
    primaryScore: scorePrimaryCandidate(candidate.evidence, normalizedHint),
    alternativeScore: scoreNearbyAlternative(candidate.evidence),
  }));
  const ranked = [...candidates].sort((a, b) =>
    b.primaryScore.total - a.primaryScore.total || a.index - b.index
  );
  // Empty-hint clicks can return only broad containing objects. Treat those as
  // "no OSM match" once penalties erase all positive evidence.
  const firstRanked = ranked[0];
  const bestCandidate =
    firstRanked !== undefined && firstRanked.primaryScore.total > 0
      ? firstRanked
      : undefined;
  if (!bestCandidate) {
    debugSelection("osm candidate scoring", () => ({
      hintName,
      normalizedHint,
      // jscpd:ignore-start
      rankedCandidates: ranked.map(({ element, index, primaryScore }) => ({
        index,
        element: {
          type: element.type,
          id: element.id,
          tags: element.tags,
        },
        lnglat: element.lnglat,
        spatialRank: element.spatialRank,
        score: primaryScore.total,
        reasons: primaryScore.reasons,
      })),
      // jscpd:ignore-end
    }));

    return { status: "empty" };
  }

  const { spatialRank: _spatialRank, ...best } = bestCandidate.element;
  const alternatives = candidates
    .filter((candidate) => !isSameOsmElement(candidate.element, best))
    .filter((candidate) => candidate.alternativeScore.total > 0)
    .sort((a, b) =>
      b.alternativeScore.total - a.alternativeScore.total || a.index - b.index
    )
    .map(({ element }) => {
      const { spatialRank: _spatialRank, ...elementWithoutSpatialRank } =
        element;
      return elementWithoutSpatialRank;
    });

  debugSelection("osm candidate scoring", () => ({
    hintName,
    normalizedHint,
    best: best,
    alternatives,
    // jscpd:ignore-start
    rankedCandidates: ranked.map(({ element, index, primaryScore }) => ({
      index,
      element: {
        type: element.type,
        id: element.id,
        tags: element.tags,
      },
      lnglat: element.lnglat,
      spatialRank: element.spatialRank,
      score: primaryScore.total,
      reasons: primaryScore.reasons,
    })),
    // jscpd:ignore-end
    rankedAlternatives: [...candidates]
      .sort((a, b) =>
        b.alternativeScore.total - a.alternativeScore.total ||
        a.index - b.index
      )
      .map(({ element, index, alternativeScore }) => ({
        index,
        element: {
          type: element.type,
          id: element.id,
          tags: element.tags,
        },
        lnglat: element.lnglat,
        spatialRank: element.spatialRank,
        score: alternativeScore.total,
        reasons: alternativeScore.reasons,
        kept: alternativeScore.total > 0 && !isSameOsmElement(element, best),
      })),
  }));

  return { status: "matched", best, alternatives };
}

function normalized(value: string | undefined): string {
  return value?.trim().toLocaleLowerCase() ?? "";
}

function osmCandidateEvidence(
  element: OsmLookupElement,
): OsmCandidateEvidence {
  const tags = element.tags;
  const name = normalized(
    firstTag(tags, ["name", "name:en", "brand", "operator"]),
  );
  const alternateNames = [
    tags["alt_name"],
    tags["official_name"],
    tags["short_name"],
    tags["name:en"],
  ].map(normalized);
  const spatialRank = element.spatialRank;
  const spatialScore = spatialMatchScore(spatialRank);
  const boundaryValue = containingBoundaryValue(tags);
  const hasLocalTag = hasLocalFeatureTag(tags);

  return {
    name,
    alternateNames,
    spatialRank,
    spatialScore,
    hasWebsite: Boolean(tags.website || tags["contact:website"]),
    hasOpeningHours: Boolean(tags.opening_hours),
    hasAddress: osmAddressLines(tags).length > 0,
    hasLocalFeatureTag: hasLocalTag,
    hasPoiCategory: Boolean(tags.amenity || tags.shop || tags.tourism),
    containingBoundaryValue: boundaryValue,
    containingBoundaryPenalty: boundaryValue
      ? boundaryPenalty(boundaryValue, tags)
      : 0,
    isRouteRelation: Boolean(tags.route),
    isUnnamedResidentialLanduse: !name &&
      spatialRank === OSM_SPATIAL_RANK.POLYGON_COVERS_CLICK &&
      tags.landuse === "residential" &&
      !hasLocalTag,
  };
}

function scorePrimaryCandidate(
  evidence: OsmCandidateEvidence,
  normalizedHint: string,
): OsmCandidateScore {
  const score = baseCandidateScore(evidence, {
    penalizeBoundaryWithoutNameHint: !normalizedHint,
  });

  if (normalizedHint && evidence.name === normalizedHint) {
    score.total += 120;
    score.reasons.unshift("+120 exact primary name match");
  } else if (
    normalizedHint && evidence.alternateNames.includes(normalizedHint)
  ) {
    score.total += 100;
    score.reasons.unshift("+100 alternate name match");
  } else if (
    normalizedHint && evidence.name &&
    (evidence.name.includes(normalizedHint) ||
      normalizedHint.includes(evidence.name))
  ) {
    score.total += 60;
    score.reasons.unshift("+60 partial name match");
  }
  return score;
}

function scoreNearbyAlternative(
  evidence: OsmCandidateEvidence,
): OsmCandidateScore {
  return baseCandidateScore(evidence, {
    penalizeBoundaryWithoutNameHint: true,
  });
}

function baseCandidateScore(
  evidence: OsmCandidateEvidence,
  options: { penalizeBoundaryWithoutNameHint: boolean },
): OsmCandidateScore {
  let score = 0;
  const reasons: string[] = [];

  if (evidence.spatialScore > 0) {
    score += evidence.spatialScore;
    reasons.push(
      `+${evidence.spatialScore} spatial match: ${
        osmSpatialMatchLabel(evidence.spatialRank)
      }`,
    );
  }
  if (evidence.name) {
    score += 20;
    reasons.push("+20 has display name");
  }
  if (evidence.hasWebsite) {
    score += 10;
    reasons.push("+10 has website");
  }
  if (evidence.hasOpeningHours) {
    score += 8;
    reasons.push("+8 has opening hours");
  }
  if (evidence.hasAddress) {
    score += 8;
    reasons.push("+8 has address");
  }
  if (evidence.hasLocalFeatureTag) {
    score += 12;
    reasons.push("+12 has local feature tag");
  }
  if (evidence.hasPoiCategory) {
    score += 6;
    reasons.push("+6 has POI category");
  }
  if (evidence.containingBoundaryValue) {
    // Boundaries often spatially contain the click without being the clicked
    // thing. A province, country, or huge protected area should not beat a
    // generic building click. Lifecycle-prefixed boundary tags count too.
    score -= evidence.containingBoundaryPenalty;
    reasons.push(`-${evidence.containingBoundaryPenalty} containing boundary`);
    if (options.penalizeBoundaryWithoutNameHint) {
      score -= 80;
      reasons.push("-80 boundary without name hint");
    }
  }
  if (evidence.isRouteRelation) {
    // Route relations can span whole cities or countries, so bbox hits are weak
    // evidence. Primary scoring can still rescue exact route-name matches.
    score -= 45;
    reasons.push("-45 route relation");
  }
  if (evidence.isUnnamedResidentialLanduse) {
    // Residential landuse describes the predominant use of a broad area. With
    // no name or more specific feature tag, it is context rather than a likely
    // click target.
    score -= 50;
    reasons.push("-50 unnamed residential landuse");
  }

  return { total: score, reasons };
}

function spatialMatchScore(rank: number): number {
  // Spatial ranks come from the PostPass candidate SQL. See ADR-008 for why
  // covering the clicked coordinate should beat nearby but non-clicked features.
  switch (rank) {
    case OSM_SPATIAL_RANK.POLYGON_COVERS_CLICK:
      return 80;
    case OSM_SPATIAL_RANK.POINT_IN_LOOKUP_BBOX:
      return 16;
    case OSM_SPATIAL_RANK.LINE_INTERSECTS_LOOKUP_BBOX:
      return 8;
    default:
      return 0;
  }
}

function hasLocalFeatureTag(tags: OsmTags): boolean {
  return Boolean(
    tags.amenity ||
      tags.shop ||
      tags.tourism ||
      tags.leisure ||
      tags.historic ||
      tags.office ||
      tags.craft ||
      tags.natural ||
      tags.building ||
      tags.highway,
  );
}

function boundaryPenalty(
  boundaryValue: string,
  tags: OsmTags,
): number {
  if (boundaryValue !== "administrative") return 60;

  const adminLevel = tags["admin_level"];
  const level = Number(adminLevel);
  if (!Number.isFinite(level)) return 30;
  if (level <= 4) return 50;
  if (level <= 6) return 35;
  if (level <= 8) return 20;
  return 10;
}

function isSameOsmElement(a: OsmElement, b: OsmElement): boolean {
  return a.type === b.type && a.id === b.id;
}
